import type {
  TechFormFieldRule,
  TechFormRuleCondition,
  TechFormSectionNumber,
  TechFormValidationConfig,
  TechFormValidationIssue,
  TechFormValidationValues,
  TechFormValidator,
} from "../models/tech-form-validation.models";

// Self-contained so the offline generator can embed this same factory as browser JavaScript.
// Keep all runtime dependencies inside the factory or in its serializable configuration.
export function createTechFormValidator(config: TechFormValidationConfig): TechFormValidator {
  const decimalPattern = new RegExp(config.patterns.decimal);
  const imoPattern = new RegExp(config.patterns.imo);
  const datePattern = new RegExp(config.patterns.date);
  const text = (value: unknown): string => (value === null || value === undefined ? "" : String(value).trim());
  const blank = (value: unknown): boolean =>
    value === null || value === undefined || (typeof value === "string" && value.trim() === "");
  const getValue = (record: unknown, key: string): unknown =>
    key
      .split(".")
      .reduce<unknown>(
        (value, part) =>
          value !== null && typeof value === "object" ? (value as Record<string, unknown>)[part] : undefined,
        record,
      );
  const readNumber = (value: unknown): number | null => {
    if ((typeof value !== "string" && typeof value !== "number") || !decimalPattern.test(text(value))) return null;
    const number = Number(text(value).replace(",", "."));
    return Number.isFinite(number) ? number : null;
  };
  const matchesCondition = (condition: TechFormRuleCondition | undefined, values: TechFormValidationValues): boolean =>
    !condition || String(getValue(values.sections[condition.section], condition.key) ?? "") === condition.value;
  const validDate = (value: unknown): boolean => {
    if (value instanceof Date) return Number.isFinite(value.getTime());
    if (typeof value !== "string") return false;
    const match = text(value).match(datePattern);
    if (!match) return false;
    const year = Number(match[1]),
      month = Number(match[2]),
      day = Number(match[3]);
    const date = new Date(0);
    date.setUTCHours(0, 0, 0, 0);
    date.setUTCFullYear(year, month - 1, day);
    return (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day &&
      Number.isFinite(Date.parse(text(value)))
    );
  };
  const calendarDay = (value: unknown): string | null => {
    if (!validDate(value)) return null;
    // Stored timestamps and date-only strings refer to the calendar date in their YYYY-MM-DD prefix.
    if (!(value instanceof Date)) return text(value).slice(0, 10);
    return (
      String(value.getFullYear()).padStart(4, "0") +
      "-" +
      String(value.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(value.getDate()).padStart(2, "0")
    );
  };
  const fieldError = (rule: Omit<TechFormFieldRule, "section">, value: unknown, required: boolean): string => {
    const candidate = rule.type === "option" ? getValue(value, "value") : value;
    if (blank(candidate)) return required ? "This field is required" : "";
    if (rule.type === "choice" || rule.type === "option") {
      if (typeof candidate !== "string" && typeof candidate !== "number") return "Select a valid option";
      return rule.choices && !rule.choices.some((choice) => choice === String(candidate))
        ? "Select " + rule.choices.join(" or ")
        : "";
    }
    if (rule.type === "imo")
      return (typeof value === "string" || typeof value === "number") &&
        (text(value) === config.fakeImo || imoPattern.test(text(value)))
        ? ""
        : "IMO number must be exactly 7 digits";
    if (rule.type === "date") return validDate(value) ? "" : "Enter a valid calendar date";
    if (rule.type === "gtNt") {
      if (typeof value !== "string") return "Enter two numbers in GT / NT format";
      const pair = text(value).split("/");
      return pair.length === 2 && pair.every((part) => readNumber(part) !== null)
        ? ""
        : "Enter two numbers in GT / NT format";
    }
    if (rule.type === "text") return typeof value === "string" ? "" : "Enter a text value";
    const number = readNumber(value);
    if (number === null) return "Enter a valid number";
    if (rule.type === "integer" && !Number.isInteger(number)) return "Enter a whole number";
    const minOk = rule.min === undefined || (rule.minInclusive ? number >= rule.min : number > rule.min);
    const maxOk = rule.max === undefined || (rule.maxInclusive ? number <= rule.max : number < rule.max);
    if (minOk && maxOk) return "";
    const limits = [];
    if (rule.min !== undefined) limits.push("greater than " + (rule.minInclusive ? "or equal to " : "") + rule.min);
    if (rule.max !== undefined) limits.push("less than " + (rule.maxInclusive ? "or equal to " : "") + rule.max);
    return "Value must be " + limits.join(" and ");
  };
  const validateSection = (
    section: TechFormSectionNumber,
    values: TechFormValidationValues,
  ): TechFormValidationIssue[] => {
    const issues: TechFormValidationIssue[] = [];
    const sectionFields = config.fields.filter((rule) => rule.section === section);
    const form = values.sections[section] || {};
    const addIssue = (key: string, label: string, message: string): void => {
      if (message && !issues.some((issue) => issue.key === key)) issues.push({ section, key, label, message });
    };
    sectionFields.forEach((rule) => {
      if (!matchesCondition(rule.when, values)) return;
      const isRequiredForSchemaVersion =
        rule.requiredFromSchemaVersion === undefined ||
        Number(values.schemaVersion ?? 0) >= rule.requiredFromSchemaVersion;
      const required =
        (rule.required && isRequiredForSchemaVersion) ||
        Boolean(rule.requiredWhenAny?.some((key) => !blank(getValue(form, key))));
      addIssue(rule.key, rule.label, fieldError(rule, getValue(form, rule.key), required));
    });
    config.relations
      .filter((rule) => rule.section === section)
      .forEach((rule) => {
        if (!matchesCondition(rule.when, values)) return;
        if (rule.operator === "dateLt") {
          const left = calendarDay(getValue(form, rule.key));
          const right = calendarDay(getValue(values.sections[rule.rightSection], rule.rightKey));
          if (left !== null && right !== null && left >= right)
            addIssue(rule.key, sectionFields.find((field) => field.key === rule.key)?.label || rule.key, rule.message);
          return;
        }
        const left = readNumber(getValue(form, rule.key));
        const right = readNumber(getValue(values.sections[rule.rightSection], rule.rightKey));
        if (left === null || right === null) return;
        const valid = rule.operator === "gt" ? left > right : rule.operator === "gte" ? left >= right : left < right;
        if (!valid)
          addIssue(rule.key, sectionFields.find((field) => field.key === rule.key)?.label || rule.key, rule.message);
      });
    if (section === 3) {
      const rows = Array.isArray(values.draftRows) ? values.draftRows : [];
      if (rows.length !== config.draftCount) {
        addIssue(
          "tq3DraftTable",
          "Smaller drafts",
          "Provide exactly " + config.draftCount + " rows; explicitly remove any additional saved rows",
        );
      }
      const summerDraft = readNumber(form.summerSWDraft);
      rows.forEach((row, index) => {
        config.draftColumns.forEach((rule) =>
          addIssue(
            "tq3DraftTable." + index + "." + rule.key,
            "Draft row " + (index + 1) + ": " + rule.label,
            fieldError(rule, row?.[rule.key], true),
          ),
        );
        const draft = readNumber(row?.draft);
        const previous = index > 0 ? readNumber(rows[index - 1]?.draft) : null;
        if (draft !== null && summerDraft !== null && draft >= summerDraft)
          addIssue("tq3DraftTable." + index + ".draft", "Draft row " + (index + 1), "Draft must be below summer draft");
        if (draft !== null && previous !== null && previous - draft !== 1)
          addIssue(
            "tq3DraftTable." + index + ".draft",
            "Draft row " + (index + 1),
            "Drafts must descend in consecutive 1 metre steps",
          );
      });
    }
    if (section === 8) {
      const rows = Array.isArray(values.powerRows) ? values.powerRows : [];
      const gensetPower = readNumber(form.auxGensetsPower);
      config.powerRows.forEach((expected) => {
        if (!matchesCondition(expected.when, values)) return;
        const namedIndex = rows.findIndex((row) => row?.name === expected.label);
        const index = namedIndex >= 0 ? namedIndex : rows.findIndex((row) => Number(row?.id) === expected.id);
        const row = rows[index] || {};
        const key = "tq8PowerManagementTable." + (index >= 0 ? index : expected.id - 1) + ".powerConsumptions";
        const countKey = "tq8PowerManagementTable." + (index >= 0 ? index : expected.id - 1) + ".noOfItems";
        addIssue(
          countKey,
          expected.label + ": number of items",
          fieldError(
            { key: countKey, label: expected.label, type: "integer", required: false, min: 0, minInclusive: true },
            row.noOfItems,
            false,
          ),
        );
        const label = expected.label + ": power consumption";
        const hasZeroItems = readNumber(row.noOfItems) === 0;
        const error = fieldError(
          {
            key,
            label,
            type: "number",
            required: expected.required && !hasZeroItems,
            min: 0,
            minInclusive: hasZeroItems,
          },
          row.powerConsumptions,
          expected.required && !hasZeroItems,
        );
        addIssue(key, label, error);
        const power = readNumber(row.powerConsumptions);
        if (hasZeroItems && power !== null && power > 0)
          addIssue(key, label, "Power consumption must be zero when the number of items is zero");
        if (!hasZeroItems && power !== null && gensetPower !== null && power >= gensetPower * config.powerFraction)
          addIssue(key, label, "Power consumption must be less than 60% of auxiliary genset power");
      });
    }
    return issues;
  };
  return {
    readNumber,
    validateSection,
    validateAll: (values) => config.sections.flatMap((section) => validateSection(section, values)),
  };
}
